import {debounce, forEach, isEmpty, isEqual} from 'lodash';
import {ApiFilter, AppResourceCollection} from '@sellers-shield-home-protection/ss-core-js';
import ShowNotificationMixin from '@sellers-shield-home-protection/ss-ui-kit/src/mixins/ShowNotificationMixin';

const PAGE_SIZE = 5;

export default {
    mixins: [
        ShowNotificationMixin,
    ],

    data() {
        return {
            tableFetch: {
                PAGE_SIZE: PAGE_SIZE,
                pagination: 'enabled',
                include: [],
                scopes: {},
                orderBy: {},
                withCount: new ApiFilter({}),
                filterForm: {
                    page: 1,
                },
                loading: true,
            },
            customLoading: false,
            filtering: false,
        };
    },
    computed: {
        total() {
            return this.appResourceSearch.total ? this.appResourceSearch.total : this.appResourceSearch.length;
        },
        isLoading() {
            return this.appResourceSearch.loading || this.customLoading;
        },
        items() {
            return this.appResourceSearch.appResourceCollection;
        },
        itemsFiltered() {
            const initialFilter = this.tableFetch.filterForm.initialFilterData ?
                new ApiFilter(this.tableFetch.filterForm.initialFilterData) :
                new ApiFilter({});
            const filter = this.buildSearchFilter(this.appResourceSearch.coreAppResource, this.tableFetch.filterForm);

            return !isEqual(initialFilter, filter);
        },

        hasNextPage() {
            const lastPage = Math.ceil(
                this.appResourceSearch.vPaginator.total / this.appResourceSearch.vPaginator.perPage,
            );

            return lastPage > this.appResourceSearch.vPaginator.page;
        },

        paginate() {
            return this.total > this.tableFetch.PAGE_SIZE;
        },

        showLoadMoreSpinner() {
            return !this.filtering
                && this.appResourceSearch.loaded
                && this.hasNextPage;
        }

    },
    methods: {
        searchData() {
            const searchData = {
                include: this.tableFetch.include,
                scopes: this.tableFetch.scopes,
                pageSize: this.tableFetch.PAGE_SIZE,
                page: this.tableFetch.filterForm.page,
                orderBy: this.tableFetch.orderBy,
            };
            if (
                this.tableFetch.hasOwnProperty('pagination') && (
                    !this.tableFetch.pagination ||
                    this.tableFetch.pagination === 'disabled'
                )
            ) {
                searchData.pagination = 'disabled';
            }
            if (this.tableFetch.withCount.isValid) {
                searchData.withCount = this.tableFetch.withCount;
            }
            const appResource = this.appResourceSearch.coreAppResource;
            const apiFilter = this.buildSearchFilter(appResource, this.tableFetch.filterForm);
            if (apiFilter !== null && apiFilter.hasData()) {
                searchData.filter = apiFilter;
            }
            return searchData;
        },
        buildSearchFilter(appResource, formData) {
            
            if (isEmpty(formData)) {
                return null;
            }
            const filter = formData.initialFilterData ? new ApiFilter(formData.initialFilterData) : new ApiFilter({});

            forEach(formData, (formItem, formKey) => {
                if (isEmpty(formItem)) {
                    return true;
                }
                
                if (appResource.relationMap().hasOwnProperty(formKey)) {
                    const RelationAppResource = appResource.relationMap()[formKey];
                    const resourceFilter = this.buildSearchFilter(new RelationAppResource(), formItem);
                    if (filter !== null) {
                        if (resourceFilter.isValid) {
                            filter.addRelation(formKey, resourceFilter);
                        }
                    }
                    return true;
                }
                if (formKey === 'scopes') {
                    for (let scope of formItem) {
                        filter.addScope(scope);
                    }
                    return true;
                }
                if (
                    appResource.fillable().indexOf(formKey) < 0 &&
                    appResource.hidden().indexOf(formKey) < 0 &&
                    formKey != 'transactionCoordinatorUuid'
                ) {
                    return true;
                }
                filter.addParam(formKey, formItem);
            });

            return filter;
        },
        filterChange() {
            if (this.tableFetch.hasOwnProperty('filterForm')) {
                this.tableFetch.filterForm.page = 1;
            }
            this.loadData();
        },
        moveToPage(page) {
            if (!this.tableFetch.hasOwnProperty('filterForm')) {
                this.tableFetch.filterForm = {};
            }
            this.tableFetch.filterForm.page = page;
            this.appResourceSearch.page = page;
        },

        changeSortDirection(orderBy) {
            this.tableFetch.orderBy = orderBy;
            this.loadData();
        },

        loadData() {
            this.appResourceSearch.setSearchData(this.searchData());
        },

        async loadDataSync() {
            await this.appResourceSearch.setSearchDataSync(this.searchData());
        },

        async orderAppResources(items) {
            if (this.itemsFiltered) {
                this.showNotification({
                    title: 'Error',
                    message: 'Please clear your filters before sorting.',
                    type: 'error',
                });
                return;
            }

            this.customLoading = true;
            const page = this.appResourceSearch.page;
            const appResourceCollection = new AppResourceCollection(this.appResourceSearch._resourceType);
            for (let i = 0; i < items.length; i++) {
                items[i].order = i + (page - 1) * this.appResourceSearch.pageSize + 1;
                appResourceCollection.addResource(items[i]);
            }
            try {
                this.appResourceSearch.appResources = items;
                await this.appResourceSearch.appResourceCollection.update();
                await this.loadData();
            } catch (e) {
                console.error(e);
                this.customLoading = false;
            }
            this.customLoading = false;
        },

        async handleScroll(visible) {
            if (visible && this.hasNextPage) {
                await this.goToNextPage();
            }
        },

        resetPagination() {
            this.appResourceCollection = new AppResourceCollection(this.appResourceSearch._resourceType);
            this.tableFetch.filterForm.page = 1;
        },

        concatenateToCollection() {
            let appResourceCollection = new AppResourceCollection(this.appResourceSearch._resourceType);

            appResourceCollection.push(this.appResourceCollection.toArray());
            appResourceCollection.push(this.appResourceSearch.appResourceCollection.toArray());

            this.appResourceCollection = appResourceCollection;
        },

        async goToNextPage() {
            let page = this.appResourceSearch.page + 1;

            this.tableFetch.filterForm.page = page;

            await this.appResourceSearch.loadPage(page);

            this.concatenateToCollection();
        },
    },
    mounted() {
        this.filterChange = debounce(this.filterChange, 1000);
    },
};
